import { useEffect, useState } from "react";
import { Button } from "../components/common";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { updateHomeGoal } from "../services/homeGoal";
import { useHomeGoalStore } from "../stores/homeGoalStore";
import { convertToNumberAndMultiplyBy100 } from "../utils/convertAmount";
import { fetchAccountData } from "../services/account";
import { useUserProfileStore } from "../stores/profileStore";

const SavingsLayout = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { setUserProfile } = useUserProfileStore();

	const [buttonDisabled, setButtonDisabled] = useState(false);
	const [showNavigation, setShowNavigation] = useState(true);
	const { targetAmount, selectedCities, propertyTypes } = useHomeGoalStore();

	const handleNextStep = async () => {
		switch (location.pathname) {
			case "/savings-input-flow/mortgage-iframe": {
				navigate("/savings-input-flow/instant-home-buying-power");
				break;
			}

			case "/savings-input-flow/instant-home-buying-power": {
				navigate("/savings-input-flow/city-selection");
				break;
			}
			case "/savings-input-flow/city-selection": {
				try {
					setButtonDisabled(true);
					const homeGoalUpdate = await updateHomeGoal({ selectedCities });
					const accountData = await fetchAccountData();
					if (accountData) {
						setUserProfile(accountData);
					}
					setButtonDisabled(false);
					navigate("/savings-input-flow/property-selection");
				} catch (error) {
					setButtonDisabled(false);
					console.error("Error updating home goal:", error);
				}
				break;
			}
			case "/savings-input-flow/property-selection": {
				try {
					setButtonDisabled(true);
					const homeGoalUpdate = await updateHomeGoal({ propertyTypes });
					const accountData = await fetchAccountData();
					if (accountData) {
						setUserProfile(accountData);
					}
					setButtonDisabled(false);
					navigate("/savings-input-flow/budget-selection");
				} catch (error) {
					setButtonDisabled(false);
					console.error("Error updating home goal:", error);
				}

				break;
			}
			case "/savings-input-flow/budget-selection": {
				try {
					setButtonDisabled(true);
					const homeGoalUpdate = await updateHomeGoal({
						targetAmount: {
							amount: convertToNumberAndMultiplyBy100(targetAmount.toString()),
							currency: "CAD",
						},
					});
					const accountData = await fetchAccountData();
					if (accountData) {
						setUserProfile(accountData);
					}
					setButtonDisabled(false);
					navigate("/savings");
				} catch (error) {
					setButtonDisabled(false);
					console.error("Error updating home goal:", error);
				}

				break;
			}
			default:
				break;
		}
	};

	useEffect(() => {
		const handleMessage = async (e: MessageEvent) => {
			if (
				e.data?.type === "CREDIT_CONSENT_SUBMITTED" &&
				location.pathname === "/savings-input-flow/mortgage-iframe"
			) {
				const accountData = await fetchAccountData();
				if (accountData) {
					setUserProfile(accountData);
				}
				const from = (
					location.state as {
						from?: string;
					}
				)?.from;
				if (from === "savings-view") {
					navigate("/savings");
				} else {
					navigate("/savings-input-flow/instant-home-buying-power");
				}
			} else if (e.data?.type === "CLOSE_MORTGAGE_APPLICATION") {
				navigate("/profile/savings");
			}
		};

		window.addEventListener("message", handleMessage);
		return () => {
			window.removeEventListener("message", handleMessage);
		};
	}, [location.pathname, navigate, location.state, setUserProfile]);
	const handlePreviousStep = () => {
		switch (location.pathname) {
			case "/savings-input-flow":
				break;
			default:
				break;
		}
		navigate(-1);
	};

	useEffect(() => {
		const path = location.pathname;
		const hideNavigationPaths = [
			"/savings-input-flow/mortgage-iframe",
			"/savings-input-flow/instant-home-buying-power",
			"/savings-input-flow/budget-selection",
		];
		setShowNavigation(!hideNavigationPaths.includes(path));

		if (
			path === "/savings-input-flow/city-selection" &&
			selectedCities.length === 0
		) {
			setButtonDisabled(true);
		} else if (
			path === "/savings-input-flow/property-selection" &&
			propertyTypes.length === 0
		) {
			setButtonDisabled(true);
		} else if (
			path === "/savings-input-flow/budget-selection" &&
			targetAmount === 0
		) {
			setButtonDisabled(true);
		} else {
			setButtonDisabled(false);
		}
	}, [location.pathname, selectedCities, propertyTypes, targetAmount]);
	return (
		<div className="overflow-auto absolute inset-0">
			<div className="relative flex justify-start h-full overflow-auto items-center pt-bridge-inset-top pb-bridge-inset-bottom">
				<Outlet />
			</div>
			{showNavigation && (
				<div
					id="footer"
					className={`fixed bottom-0 right-0 left-0 flex items-center ${
						window.bridge ? "pb-bridge-inset-bottom pt-3" : "py-4"
					} justify-between px-4 bg-white border-t border-[#F2F2F2] w-full`}
				>
					<Button
						onClick={handlePreviousStep}
						disable={buttonDisabled}
						variant="primary"
						customClassName="font-bold text-lg bg-white active:opacity-70"
						height="lg"
						isOutlined={true}
						width="120px"
						radius="60px"
					>
						Back
					</Button>

					<Button
						onClick={handleNextStep}
						variant="primary"
						customClassName="font-bold text-lg disabled:bg-[#D9D9D9] min-w-[120px] shadow-md active:opacity-70"
						height="lg"
						width="fit"
						radius="60px"
						disable={buttonDisabled}
					>
						Next
					</Button>
				</div>
			)}
		</div>
	);
};

export { SavingsLayout };
